import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { zim } from '../../../zegoConfig';
import { loginToZego } from '../../../components/utils/zegoLogin';
import axios from '../../../api/axios';
import { sendMessageToReactNativeApp } from "../../../components/utils/sendMessageToReactNativeApp";
import { ZIM as ZimType, ZIMMessage } from 'zego-zim-web';
import { decryptFromBackend } from '../../../utils/urlDecryptor';
import HeaderWithBack from '../../../components/HeaderWithBack';
import { assetUrl, imageUrl } from '../../../utils/assets';

interface TeleconsultationData {
  patient_id: string;
  patient_name: string;
  practitioner_id: string;
  practitioner_role_id: string;
  teleconsultation_start_time: string | null;
  doctor_assign_status: 'assigned' | 'unassigned';
}

interface UserData {
  id: string;
  person: {
    name: string;
  };
  practitioner_role_id: string;
}

interface DoctorOption {
  id: number;
  doctorId: string;
  doctorName: string;
  doctorSpeciality: string;
  price_chat: number;
  availabilityStatus: string;
  photoUrl: string;
  rating: number;
  experience: number;
}

type Phase = 'searching' | 'waiting_accept' | 'started';

const WAITING_TIME = 180;

const ConnectingDoctor: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{
    teleconsultationId: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return '';
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error('Failed to decrypt token:', err);
      return '';
    }
  }, [token]);

  // States
  const [userData, setUserData] = useState<UserData | null>(null);
  const [teleconsultationData, setTeleconsultationData] = useState<TeleconsultationData | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const timerStartTimeRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<Phase>('searching');
  const [fadeIn, setFadeIn] = useState(true);
  const [displayText, setDisplayText] = useState({ main: '', sub: '' });

  const [cycleCount, setCycleCount] = useState(1);
  const [showWaitBanner, setShowWaitBanner] = useState(false);
  const [showDoctorSwitchModal, setShowDoctorSwitchModal] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState<DoctorOption[]>([]);
  const [selectedSwitchDoctor, setSelectedSwitchDoctor] = useState<DoctorOption | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [switchingDoctorName, setSwitchingDoctorName] = useState('');
  const [confirmDoctor, setConfirmDoctor] = useState<DoctorOption | null>(null);
  const doctorsFetchedRef = useRef(false);
  const autoSwitchShownRef = useRef(false);
  const isSwitchingRef = useRef(false);
  const lastSwitchAtRef = useRef(0);

  const phaseTexts: Record<Phase, { main: string; sub: string }> = {
    searching: {
      main: 'Menghubungkan ke dokter Anda...',
      sub: 'Mohon tunggu, sistem sedang menghubungi dokter yang Anda pilih',
    },
    waiting_accept: {
      main: 'Menunggu dokter merespons...',
      sub: 'Dokter Anda sedang memproses permintaan konsultasi ini',
    },
    started: {
      main: 'Dokter siap!',
      sub: 'Mengalihkan ke ruang konsultasi...',
    },
  };

  const teleconsultationCacheKey = `teleconsultationData_${teleconsultationId}`;

  const currentPractitionerRoleId = teleconsultationData?.practitioner_role_id;
  const switchableDoctors = useMemo(
    () =>
      currentPractitionerRoleId
        ? availableDoctors.filter((d) => String(d.id) !== String(currentPractitionerRoleId))
        : availableDoctors,
    [availableDoctors, currentPractitionerRoleId]
  );

  const fetchAvailableDoctors = useCallback(async () => {
    if (doctorsFetchedRef.current && availableDoctors.length > 0) return;
    doctorsFetchedRef.current = true;

    try {
      const res = await axios.get('practitoner-roles', {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const raw: any[] = res.data?.data ?? [];
      const mapped: DoctorOption[] = raw.map((d: any) => ({
        id: d.id,
        doctorId: d.doctorId,
        doctorName: d.doctorName,
        doctorSpeciality: d.doctorSpeciality || 'Umum',
        price_chat: d.price_chat,
        availabilityStatus: d.availabilityStatus,
        photoUrl: d.photoUrl || `https://i.pravatar.cc/100?img=${d.id}`,
        rating: d.rating ?? 0,
        experience: d.experience ?? 0,
      }));

      setAvailableDoctors(mapped.filter((d) => d.availabilityStatus === 'ONLINE'));
    } catch (err) {
      console.error('Gagal fetch dokter:', err);
      doctorsFetchedRef.current = false; // allow retry on error
    }
  }, [decryptedToken, availableDoctors.length]);

  useEffect(() => {
    const savedStartTime = localStorage.getItem(`timer_start_${teleconsultationId}`);
    const savedCycle     = localStorage.getItem(`cycle_count_${teleconsultationId}`);
    const savedBanner    = localStorage.getItem(`show_banner_${teleconsultationId}`);
    const savedSwitchShown = localStorage.getItem(`switch_shown_${teleconsultationId}`);

    const restoredCycle = savedCycle ? parseInt(savedCycle, 10) : 1;
    setCycleCount(restoredCycle);

    if (savedBanner === '1') setShowWaitBanner(true);

    if (savedStartTime) {
      const startTime = parseInt(savedStartTime, 10);
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      const remaining = Math.max(0, WAITING_TIME - elapsedSeconds);
      timerStartTimeRef.current = startTime;
      setTimeLeft(remaining);
    } else {
      const now = Date.now();
      timerStartTimeRef.current = now;
      localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
      axios
        .get('waiting-acceptance-time', {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        })
        .then((res) => setTimeLeft(res.data.time))
        .catch(() => setTimeLeft(WAITING_TIME));
    }

    if (restoredCycle >= 2) {
      localStorage.setItem(`switch_shown_${teleconsultationId}`, '1');
    }
  }, [teleconsultationId, decryptedToken]);

  useEffect(() => {
    if (!showDoctorSwitchModal) return;
    const timer = setInterval(() => {
      doctorsFetchedRef.current = false;
      fetchAvailableDoctors();
    }, 15000);
    return () => clearInterval(timer);
  }, [showDoctorSwitchModal, fetchAvailableDoctors, teleconsultationData?.practitioner_role_id]);

  const openDoctorSwitchAutomatically = useCallback(() => {
    if (autoSwitchShownRef.current || isSwitchingRef.current) return;
    autoSwitchShownRef.current = true;
    doctorsFetchedRef.current = false;
    fetchAvailableDoctors();
    setShowDoctorSwitchModal(true);
  }, [fetchAvailableDoctors]);

  const syncWaitingTimer = useCallback(
    (waitingSeconds: number) => {
      if (isSwitchingRef.current) return;

      const serverCycle = Math.floor(waitingSeconds / WAITING_TIME) + 1;
      const elapsedInCycle = waitingSeconds % WAITING_TIME;
      const startTime = Date.now() - elapsedInCycle * 1000;

      timerStartTimeRef.current = startTime;
      localStorage.setItem(`timer_start_${teleconsultationId}`, startTime.toString());
      localStorage.setItem(`cycle_count_${teleconsultationId}`, serverCycle.toString());
      setCycleCount(serverCycle);
      setTimeLeft(WAITING_TIME - elapsedInCycle);

      if (serverCycle >= 2) {
        openDoctorSwitchAutomatically();
      }
    },
    [teleconsultationId, openDoctorSwitchAutomatically]
  );
  const syncWaitingTimerRef = useRef(syncWaitingTimer);
  syncWaitingTimerRef.current = syncWaitingTimer;

  useEffect(() => {
    if (!isDataLoaded) return;
    const savedCycle = localStorage.getItem(`cycle_count_${teleconsultationId}`);
    const restoredCycle = savedCycle ? parseInt(savedCycle, 10) : 1;

    if (restoredCycle >= 2) {
      openDoctorSwitchAutomatically();
    }
  }, [isDataLoaded, teleconsultationId, openDoctorSwitchAutomatically]);

  useEffect(() => {
    if (timeLeft === null || timerStartTimeRef.current === null) return;

    if (timeLeft <= 0) {
      const nextCycle = cycleCount + 1;
      setCycleCount(nextCycle);
      localStorage.setItem(`cycle_count_${teleconsultationId}`, nextCycle.toString());

      const now = Date.now();
      timerStartTimeRef.current = now;
      localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
      setTimeLeft(WAITING_TIME);

      // Kirim notifikasi pengingat ke dokter saat ini tanpa mengubah dokter
      axios
        .post(`teleconsult/${teleconsultationId}/remind`, {}, {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        })
        .catch((err) => console.error('Gagal mengirim pengingat:', err));

      if (nextCycle >= 2) {
        setShowWaitBanner(true);
        localStorage.setItem(`show_banner_${teleconsultationId}`, '1');
        autoSwitchShownRef.current = true;
        doctorsFetchedRef.current = false;
        fetchAvailableDoctors();
        setShowDoctorSwitchModal(true);
        localStorage.setItem(`switch_shown_${teleconsultationId}`, '1');
      }

      return;
    }

    const timer = setInterval(() => {
      if (timerStartTimeRef.current === null) return;
      const elapsed = Math.floor((Date.now() - timerStartTimeRef.current) / 1000);
      const remaining = Math.max(0, WAITING_TIME - elapsed);
      setTimeLeft(remaining);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, teleconsultationId, cycleCount, decryptedToken, fetchAvailableDoctors, teleconsultationData]);

  // ─── Refs ──────────────────────────────────────────────────────────────────
  const previousUserDokterRef = useRef<string | null>(null);
  const isCancelledRef = useRef(false);
  // Cegah navigasi ganda ke room chat (polling & event ZIM bisa memicu bersamaan)
  const hasNavigatedRef = useRef(false);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  }, []);

  const handleNavigationError = useCallback(
    (error: any) => {
      if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
        navigate('/not-found');
      } else if (error.status === 403) {
        navigate('/forbidden');
      } else {
        console.error('Unexpected error:', error);
      }
    },
    [navigate]
  );

  const fetchUserData = useCallback(async (): Promise<UserData | null> => {
    if (!decryptedToken) return null;
    try {
      const response = await axios.post('users', null, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const data = response.data.data;
      localStorage.setItem('userData', JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, handleNavigationError]);

  const fetchTeleconsultationData = useCallback(async (): Promise<TeleconsultationData | null> => {
    if (!decryptedToken || !teleconsultationId) return null;
    try {
      const response = await axios.get(`teleconsult/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });
      const data = response.data;
      localStorage.setItem(teleconsultationCacheKey, JSON.stringify(data));
      return data;
    } catch (error) {
      handleNavigationError(error);
      return null;
    }
  }, [decryptedToken, teleconsultationId, teleconsultationCacheKey, handleNavigationError]);

  const isValidUserData = (data: any): data is UserData =>
    data && data.id && data.person?.name && data.practitioner_role_id;

  const isValidTeleconsultationData = (data: any): data is TeleconsultationData =>
    data && data.patient_id && data.patient_name && data.practitioner_id;

  // ─── ZIM ───────────────────────────────────────────────────────────────────

  // Event ZIM yang dipakai halaman ini. Nama harus persis seperti yang di-emit
  // SDK zego-zim-web, dan wajib dilepas saat keluar halaman supaya tidak
  // menimpa listener milik halaman chat.
  const ZIM_EVENTS = useMemo(
    () => [
      'peerMessageReceived',
      'receivePeerMessage',
      'groupMessageReceived',
      'receiveGroupMessage',
      'conversationChanged',
    ],
    []
  );

  const clearZimListeners = useCallback(() => {
    // Selalu lepas per nama event. zim.off() tanpa argumen akan membuat SDK
    // memanggil leaveGroup internal dan pesan dokter jadi tidak masuk.
    ZIM_EVENTS.forEach((eventName) => {
      try {
        zim.off(eventName);
      } catch (err) {
        console.warn('Gagal melepas listener ZIM:', eventName, err);
      }
    });
  }, [ZIM_EVENTS]);

  const goToChatRoom = useCallback(() => {
    if (!teleconsultationId) return;
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;

    // Serahkan kendali event ZIM ke halaman chat sebelum berpindah.
    clearZimListeners();
    navigate(`/chat/${teleconsultationId}/${token}`);
  }, [clearZimListeners, navigate, teleconsultationId, token]);

  const setupConversationListener = useCallback(
    (targetUserId: string) => {
      if (isCancelledRef.current || hasNavigatedRef.current) return;

      clearZimListeners();

      const isAcceptedForThisConsultation = (message: unknown): boolean => {
        if (typeof message !== 'string' || !message.startsWith('accepted')) return false;
        // Format pesan: "accepted|{teleconsultation_id}".
        // Bandingkan id secara utuh agar id lain yang berawalan sama tidak ikut cocok.
        const id = message.split('|')[1]?.trim();
        return !!id && id === teleconsultationId;
      };

      const handleIncoming = (_zim: any, data: any) => {
        const messageList: ZIMMessage[] = data?.messageList ?? [];
        const accepted = messageList.some((msg: any) =>
          isAcceptedForThisConsultation(msg?.message)
        );
        if (accepted) {
          goToChatRoom();
        }
      };

      // Pesan "accepted|{id}" dikirim backend sebagai pesan peer atau pesan
      // group, tergantung flag rumah sakit. Dengarkan keduanya supaya pasien
      // langsung masuk room tanpa menunggu polling 5 detik.
      zim.on('peerMessageReceived', handleIncoming);
      zim.on('receivePeerMessage', handleIncoming);
      zim.on('groupMessageReceived', handleIncoming);
      zim.on('receiveGroupMessage', handleIncoming);

      zim.on('conversationChanged', async () => {
        if (isCancelledRef.current || hasNavigatedRef.current) return;
        const config = { nextMessage: 0, count: 30, reverse: true };
        try {
          const chatHistory = await zim.queryHistoryMessage(targetUserId, 0, config);
          const accepted = chatHistory.messageList.filter(
            (msg: any) => typeof msg.message === 'string' && msg.message.startsWith('accepted')
          );
          accepted.forEach((msg: any) => {
            const id = msg.message.split('|')[1]?.trim();
            if (id === teleconsultationId) {
              goToChatRoom();
            }
          });
        } catch (err) {
          console.error('Gagal ambil riwayat pesan:', err);
        }
      });
    },
    [clearZimListeners, goToChatRoom, teleconsultationId]
  );

  const loginAndSetupZIM = useCallback(async () => {
    if (!teleconsultationData || !process.env.REACT_APP_ZEGOCLOUD_APP_ENV) return;
    if (!isValidTeleconsultationData(teleconsultationData)) return;

    try {
      const envSuffix = process.env.REACT_APP_ZEGOCLOUD_APP_ENV || '';
      const userId = `LMS${teleconsultationData.patient_id}${envSuffix}`;
      const username = teleconsultationData.patient_name;
      const practitionerId = `LMS${teleconsultationData.practitioner_id}${envSuffix}`;

      const loginSuccess = await loginToZego(userId, username);
      if (loginSuccess) {
        setupConversationListener(practitionerId);
        previousUserDokterRef.current = practitionerId;
      }
    } catch (error) {
      console.error('Login ZIM gagal:', error);
    }
  }, [teleconsultationData, setupConversationListener]);

  // ─── Initial data load ─────────────────────────────────────────────────────
  const loadInitialData = useCallback(async () => {
    try {
      const savedUserData = localStorage.getItem('userData');
      const savedTeleconsultationData = localStorage.getItem(teleconsultationCacheKey);

      let userDataValid = false;
      let teleconsultationDataValid = false;

      if (savedUserData) {
        try {
          const parsed = JSON.parse(savedUserData);
          if (isValidUserData(parsed)) {
            setUserData(parsed);
            userDataValid = true;
          }
        } catch {}
      }

      if (savedTeleconsultationData) {
        try {
          const parsed = JSON.parse(savedTeleconsultationData);
          if (isValidTeleconsultationData(parsed)) {
            setTeleconsultationData(parsed);
            teleconsultationDataValid = true;
          }
        } catch {}
      }

      if (!userDataValid || !teleconsultationDataValid) {
        const promises = [];
        if (!userDataValid) promises.push(fetchUserData());
        promises.push(fetchTeleconsultationData());

        const results = await Promise.all(promises);
        let idx = 0;
        if (!userDataValid && results[idx]) {
          setUserData(results[idx] as UserData);
          idx++;
        }
        if (!teleconsultationDataValid && results[idx]) {
          setTeleconsultationData(results[idx] as TeleconsultationData);
        }
      }

      setIsDataLoaded(true);
    } catch {
      setIsDataLoaded(true);
    }
  }, [fetchUserData, fetchTeleconsultationData, teleconsultationCacheKey]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Saat meninggalkan halaman ini, lepaskan seluruh listener ZIM.
  // Tanpa ini listener lama tetap memegang event ZIM, sehingga pesan yang
  // masuk di halaman chat tidak pernah sampai ke handler halaman chat dan
  // percakapan baru terlihat setelah halaman di-refresh.
  useEffect(() => {
    isCancelledRef.current = false;

    return () => {
      isCancelledRef.current = true;
      clearZimListeners();
    };
  }, [clearZimListeners]);

  // ─── Polling ───────────────────────────────────────────────────────────────
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const updatePhaseWithFade = (newPhase: Phase) => {
    setFadeIn(false);
    setTimeout(() => {
      setDisplayText(phaseTexts[newPhase]);
      setPhase(newPhase);
      setFadeIn(true);
    }, 400);
  };

  useEffect(() => {
    if (!isDataLoaded || !teleconsultationId) return;

    const poll = async () => {
      const requestedAt = Date.now();
      try {
        const res = await axios.get(`teleconsult/${teleconsultationId}/assign-status`, {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        });
        const { phase: serverPhase, waiting_seconds: waitingSeconds } = res.data;

        if (serverPhase === 'waiting_payment') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          navigate(`/payment-success?teleconsult_id=${teleconsultationId}&enc_token=${token}`, { replace: true });
          return;
        }

        if (serverPhase === 'started') {
          if (hasNavigatedRef.current) return;
          updatePhaseWithFade('started');
          if (pollingRef.current) clearInterval(pollingRef.current);

          // Pastikan ZIM session siap sebelum navigasi untuk menghindari chat kosong
          loginAndSetupZIM().finally(() => {
            setTimeout(() => goToChatRoom(), 500);
          });
          return;
        }
        if (
          serverPhase === 'waiting_accept' &&
          typeof waitingSeconds === 'number' &&
          !hasNavigatedRef.current &&
          requestedAt > lastSwitchAtRef.current
        ) {
          syncWaitingTimerRef.current(waitingSeconds);
        }
        if (serverPhase === 'waiting_accept' && phase !== 'waiting_accept') {
          if (hasNavigatedRef.current) return;
          updatePhaseWithFade('waiting_accept');
          loginAndSetupZIM();
          return;
        }
        if (serverPhase === 'searching') {
          updatePhaseWithFade('searching');
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    setDisplayText(phaseTexts['searching']);
    poll();
    pollingRef.current = setInterval(poll, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isDataLoaded, teleconsultationId]);

  const handleSelectDoctor = (doctor: DoctorOption) => {
    setConfirmDoctor(doctor);
  };

  const handleConfirmSwitch = async () => {
    if (!confirmDoctor) return;
    const doctor = confirmDoctor;
    setConfirmDoctor(null);
    setShowDoctorSwitchModal(false);
    setSwitchError(null);
    setIsSwitching(true);
    isSwitchingRef.current = true;
    setSwitchingDoctorName(doctor.doctorName);

    const showSwitchFailure = (message?: string) => {
      isSwitchingRef.current = false;
      setIsSwitching(false);
      setSwitchingDoctorName('');
      setSwitchError(message || 'Dokter tidak dapat dipilih saat ini. Silakan pilih dokter lain.');
      setAvailableDoctors([]);
      doctorsFetchedRef.current = false;
      fetchAvailableDoctors();
      setShowDoctorSwitchModal(true);
    };

    try {
      const response = await axios.post(
        'teleconsultation/change-practitoner-roles',
        {
          doctor_code: doctor.doctorId,
          teleconsultation_id: teleconsultationId,
        },
        { headers: { Authorization: `Bearer ${decryptedToken}` } }
      );

      if (response.data?.code === 200) {
        localStorage.removeItem(`timer_start_${teleconsultationId}`);
        localStorage.removeItem(`cycle_count_${teleconsultationId}`);
        localStorage.removeItem(`show_banner_${teleconsultationId}`);
        localStorage.removeItem(`switch_shown_${teleconsultationId}`);
        localStorage.removeItem(teleconsultationCacheKey); // force re-fetch data teleconsult baru

        setCycleCount(1);
        setShowWaitBanner(false);
        lastSwitchAtRef.current = Date.now();
        isSwitchingRef.current = false;
        setIsSwitching(false);
        setSwitchingDoctorName('');

        const now = Date.now();
        timerStartTimeRef.current = now;
        localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
        setTimeLeft(WAITING_TIME);

        const freshData = await fetchTeleconsultationData();
        if (freshData) setTeleconsultationData(freshData);

      } else {
        console.error('Gagal ganti dokter:', response.data?.message);
        showSwitchFailure(response.data?.message);
      }
    } catch (error: any) {
      console.error('Error beralih dokter:', error);
      showSwitchFailure(error?.response?.data?.message);
    }
  };

  // ─── Retry / Back ──────────────────────────────────────────────────────────
  const handleBack = () => {
    sendMessageToReactNativeApp({ action: 'goBack' });
    navigate('/home');
  };

  const handleRetry = () => {
    setIsTimeout(false);
    setCycleCount(1);
    setShowWaitBanner(false);
    setShowDoctorSwitchModal(false);
    doctorsFetchedRef.current = false;
    localStorage.removeItem(`cycle_count_${teleconsultationId}`);
    localStorage.removeItem(`show_banner_${teleconsultationId}`);
    localStorage.removeItem(`switch_shown_${teleconsultationId}`);

    const now = Date.now();
    timerStartTimeRef.current = now;
    localStorage.setItem(`timer_start_${teleconsultationId}`, now.toString());
    setTimeLeft(WAITING_TIME);
    axios
      .post(`teleconsult/${teleconsultationId}/remind`, {}, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      })
      .catch((err) => console.error('Gagal mengirim pengingat:', err));
  };

  const getLoadingSubText = () => {
    if (isSwitching) return `Menunggu ${switchingDoctorName} menerima konsultasi Anda...`;
    if (cycleCount >= 2) return 'Dokter belum merespons. Anda dapat menunggu atau beralih ke dokter lain.';
    return displayText.sub;
  };

  const getLoadingMainText = () => {
    if (isSwitching) return `Beralih ke ${switchingDoctorName}...`;
    if (cycleCount >= 2) return 'Dokter belum merespons...';
    return displayText.main;
  };

  const showMainUI = !isTimeout || phase === 'searching' || phase === 'waiting_accept';

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex flex-col items-center justify-between">
      <div className="fixed top-0 left-0 right-0 z-[10000]">
        <HeaderWithBack title="Connect Doctor" url="/home" />
      </div>

      <div className="flex flex-col items-center justify-center flex-grow mt-4 px-4 text-center">
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">

          {showMainUI && (
            <div className="flex flex-col items-center gap-4 px-6 w-full max-w-sm">
              <img
                src={assetUrl("logo-linksehat.png")}
                alt="Loading"
                className="w-20 h-20 animate-pulse"
              />

              {timeLeft !== null && (
                <p className="text-2xl font-semibold text-black">{formatTime(timeLeft)}</p>
              )}

              <p className={`font-bold text-xl transition-opacity duration-500 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {getLoadingMainText()}
              </p>
              <p className={`text-sm text-gray-500 transition-opacity duration-500 delay-100 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}>
                {getLoadingSubText()}
              </p>

              {cycleCount >= 2 && !isSwitching && !showDoctorSwitchModal && (
                <button
                  onClick={() => {
                    setSwitchError(null);
                    doctorsFetchedRef.current = false;
                    fetchAvailableDoctors();
                    setShowDoctorSwitchModal(true);
                  }}
                  className="mt-1 text-sm text-teal-600 underline underline-offset-2"
                >
                  Pilih dokter lain
                </button>
              )}
            </div>
          )}

          {isTimeout && phase !== 'searching' && phase !== 'waiting_accept' && (
            <div className="absolute inset-0 bg-black/40 flex items-end z-50">
              <div className="bg-white rounded-t-2xl w-full p-6 relative">
                <button
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-lg"
                  onClick={handleBack}
                >
                  ✕
                </button>
                <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-white text-4xl">✖</span>
                </div>
                <h2 className="mt-4 text-lg font-bold text-center text-black">Gagal Menghubungkan</h2>
                <p className="text-sm text-gray-500 text-center mt-1">
                  Saat ini kami gagal menghubungkan Anda ke dokter. Silakan coba lagi.
                </p>
                <div className="flex gap-4 mt-6">
                  <button onClick={handleBack} className="flex-1 py-2 rounded-full border border-teal-500 text-teal-500">
                    Kembali
                  </button>
                  <button onClick={handleRetry} className="flex-1 py-2 rounded-full bg-teal-500 text-white">
                    Coba Lagi
                  </button>
                </div>
              </div>
            </div>
          )}

          {showDoctorSwitchModal && (
            <div className="absolute inset-0 bg-black/40 flex items-end z-50">
              <div className="bg-white rounded-t-2xl w-full max-h-[70vh] flex flex-col">
                <div className="relative p-5 border-b border-gray-100">
                  <button
                    onClick={() => setShowDoctorSwitchModal(false)}
                    className="absolute right-5 top-5 text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ✕
                  </button>

                  <div className="text-center px-6">
                    <h2 className="text-base font-bold text-gray-800">
                      Dokter lain tersedia
                    </h2>

                    <p className="text-xs text-gray-500 mt-1">
                      Pencarian membutuhkan waktu lebih lama. Pilih dokter lain yang siap membantu Anda sekarang.
                    </p>
                    {switchError && (
                      <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-2">{switchError}</p>
                    )}
                  </div>
                </div>

                <div className="overflow-y-auto flex-1 px-4 py-2 space-y-2">
                  {switchableDoctors.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-6">
                      Tidak ada dokter lain yang tersedia saat ini
                    </p>
                  ) : (
                    switchableDoctors.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center gap-4 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50"
                      >
                        <img
                          src={imageUrl(doc.photoUrl)}
                          alt={doc.doctorName}
                          onError={(e) => {
                            e.currentTarget.src =
                              'https://m.linksehat.com/assets/img/users/dummy.jpg';
                          }}
                          className="w-11 h-11 rounded-full object-cover flex-shrink-0 border-2 border-white shadow-sm"
                        />
                        <div className="flex-1 flex flex-col items-center text-center px-2">
                          <p className="font-semibold text-sm text-gray-800">
                            {doc.doctorName}
                          </p>

                          <p className="text-xs text-gray-500 mt-0.5">
                            {doc.doctorSpeciality}
                          </p>

                          <div className="flex items-center justify-center gap-1 mt-1">
                            <span className="text-yellow-400 text-xs">★</span>
                            <span className="text-xs text-gray-600">
                              {doc.rating.toFixed(1)}
                            </span>
                            <span className="text-gray-300 text-xs">•</span>
                            <span className="text-xs text-gray-500">
                              {doc.experience} thn pengalaman
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleSelectDoctor(doc)}
                          className="flex-shrink-0 bg-teal-500 text-white text-xs font-semibold px-4 py-2 rounded-full"
                        >
                          Pilih
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowDoctorSwitchModal(false)}
                    className="w-full py-2.5 rounded-full border border-teal-500 text-teal-500 text-sm font-medium"
                  >
                    Tetap tunggu dokter saat ini
                  </button>
                </div>
              </div>
            </div>
          )}

          {confirmDoctor && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-[60] px-5">
              <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">

                <div className="flex flex-col items-center px-5 pt-5 pb-4">
                  <img
                    src={imageUrl(confirmDoctor.photoUrl)}
                    alt={confirmDoctor.doctorName}
                    onError={(e) => {
                      e.currentTarget.src =
                        'https://m.linksehat.com/assets/img/users/dummy.jpg';
                    }}
                    className="w-14 h-14 rounded-full object-cover border-2 border-teal-100"
                  />

                  <p className="font-bold text-gray-800 text-base mt-3 text-center">
                    {confirmDoctor.doctorName}
                  </p>

                  <p className="text-sm text-gray-500 text-center">
                    {confirmDoctor.doctorSpeciality}
                  </p>

                  <div className="flex items-center justify-center gap-1.5 mt-2">
                    <span className="text-yellow-400 text-xs">★</span>
                    <span className="text-xs text-gray-600">
                      {confirmDoctor.rating.toFixed(1)}
                    </span>

                    <span className="text-gray-300 text-xs">•</span>

                    <span className="text-xs text-gray-500">
                      {confirmDoctor.experience} thn pengalaman
                    </span>
                  </div>
                </div>

                <div className="border-t border-gray-100 px-6 py-4">
                  <h3 className="text-base font-bold text-gray-800 text-center">
                    Pindah ke dokter ini?
                  </h3>

                  <p className="text-sm text-gray-500 text-center mt-3 leading-relaxed max-w-xs mx-auto">
                    Anda akan beralih konsultasi ke{' '}
                    <span className="font-semibold text-gray-700">
                      {confirmDoctor.doctorName}
                    </span>.
                    <br />
                    Proses pencarian akan dimulai ulang dengan dokter baru.
                  </p>
                </div>

                <div className="flex border-t border-gray-100">
                  <button
                    onClick={() => setConfirmDoctor(null)}
                    className="flex-1 py-3.5 text-gray-600 text-sm font-medium border-r border-gray-100"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleConfirmSwitch}
                    className="flex-1 py-3.5 text-teal-500 text-sm font-semibold"
                  >
                    Ya, Pindah
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ConnectingDoctor;
